import React, { ReactElement, useEffect } from 'react';
import {
  Collapse,
  Spin,
} from 'antd';
import { useTranslation } from 'react-i18next';
import { useGetSegmentsDataQuery } from 'domains/segments/queries/segments.query';
import { TSegmentsResponse } from 'domains/segments/types/segments.types';
import CollapseExpandIconComponent from './collapse-expand-icon.component';
import { ETypes } from '../enums/limits.enum';
import {
  STreeWithRenderBody,
  SSpinArea,
} from '../standard-limits.page.styles';
import NoPeriodTableComponent from '../tables/no-period-table/no-period-table.component';
import PeriodTableComponent from '../tables/period-table/period-table.component';

type TTreeItem = {
  label: string,
  key: string,
  children?: TTreeItem[],
};

type TProps = {
  counterpartyData: {
    guid: string,
    name: string,
  }
};

const SegmentsTreeComponent = (props: TProps): ReactElement => {
  const { counterpartyData } = props;
  const { t } = useTranslation();

  const redoSegmentsData = (data: TSegmentsResponse): {
    originalReq: TSegmentsResponse,
    tree: {
      children: { label: string, key: ETypes }[],
      originalReq: TSegmentsResponse,
      label: string,
      key: string
    }[]
  } => ({
    originalReq: data,
    tree: Object.keys(data[0].segments).map((segment) => ({
      originalReq: data,
      label: segment,
      key: segment,
      children: [
        {
          label: t('limits.standardLimits.table.segments.title.user'),
          key: ETypes.User,
        },
        // {
        //   label: t('limits.standardLimits.table.segments.title.account'),
        //   key: ETypes.Account,
        // },
        {
          label: t('limits.standardLimits.table.segments.title.operation'),
          key: ETypes.Operation,
        }
      ],
    })),
  });

  const {
    data: segmentsData,
    isLoading: isLoadingSegmentsData,
    isError: isErrorSegmentsData,
    error: errorSegmentsData,
  } = useGetSegmentsDataQuery(counterpartyData.guid, {}, {
    select: redoSegmentsData,
  });

  useEffect(() => {
    if (isErrorSegmentsData) {
      console.error(errorSegmentsData);
    }
  }, [isErrorSegmentsData, errorSegmentsData]);

  const renderTree = (data: TTreeItem[], prevDataKey?: string[][] | undefined): ReactElement => {
    const redoData = data.map((item: TTreeItem) => {
      const { key, label, children } = item;
      const dataKey = prevDataKey
        ? [
          ...prevDataKey,
          [label, key],
        ]
        : [[counterpartyData.name, counterpartyData.guid], [label, key]];
      let typeTableRender = null;

      switch (key) {
        case ETypes.User:
          typeTableRender = <PeriodTableComponent
            dataKey={dataKey}
            tableType={ETypes.User}
            segmentsData={segmentsData.originalReq}
          />;
          break;
        // case ETypes.Account:
          // typeTableRender = <PeriodTableComponent dataKey={dataKey} tableType={ETypes.Account} />;
          // break;
        case ETypes.Operation:
          typeTableRender = <NoPeriodTableComponent
            dataKey={dataKey}
            tableType={ETypes.Operation}
            segmentsData={segmentsData.originalReq}
          />;
          break;
        default:
          break;
      }

      return {
        key,
        label,
        ...(children?.length
          ? { children: renderTree(children, dataKey) }
          : { children: typeTableRender }
        ),
      }
    });

    return <STreeWithRenderBody>
      <Collapse
        ghost
        items={redoData}
        expandIcon={CollapseExpandIconComponent}
        destroyInactivePanel
      />
    </STreeWithRenderBody>
  };

  if (isLoadingSegmentsData) {
    return <Spin>
      <SSpinArea />
    </Spin>
  }

  return renderTree(segmentsData.tree);
}

export default SegmentsTreeComponent;
